import { useDependencies, Token } from '@servicetitan/react-ioc';
import { IReactionDisposer } from 'mobx';
import { useEffect, useMemo } from 'react';

export interface InitStore<Prop1 = void> {
    init?: Prop1 extends void ? () => void | Promise<void> : (prop1: Prop1) => void | Promise<void>;
    initDisposer?(): IReactionDisposer | IReactionDisposer[];
    destroy?(): void;
}

export function useInitDependency<Prop1, T extends InitStore<Prop1>>(
    storeClass: Token<T>,
    prop1: Prop1
): T;
export function useInitDependency<T extends InitStore>(storeClass: Token<T>): T;
export function useInitDependency(storeClass: any, prop1?: any) {
    const [store] = useDependencies<[any]>(storeClass);
    // init should be called only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoProp1 = useMemo(() => prop1, []);

    useEffect(() => {
        const result = store.init?.(memoProp1);

        if (result?.catch) {
            result.catch(() => null);
        }

        const disposers = store.initDisposer?.();

        return () => {
            store.destroy?.();

            if (disposers) {
                if (Array.isArray(disposers)) {
                    disposers.forEach(disposer => disposer());
                } else {
                    disposers();
                }
            }
        };
    }, [store, memoProp1]);

    return store;
}
